import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useTheme } from '@/context/ThemeContext';
import axios from 'axios';
import { Check, Loader2, AlertTriangle, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const { refreshUser } = useAuth();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking');
  const [txn, setTxn] = useState(null);
  const attemptsRef = useRef(0);

  const pollPayment = useCallback(async () => {
    if (attemptsRef.current >= 8) { setStatus('timeout'); return; }
    attemptsRef.current += 1;
    try {
      const { data } = await axios.get(`${API}/api/payments/status/${sessionId}`, { withCredentials: true });
      setTxn(data);
      if (data.payment_status === 'paid') {
        setStatus('success');
        await refreshUser();
        return;
      }
      if (data.status === 'expired') { setStatus('expired'); return; }
      setTimeout(pollPayment, 2000);
    } catch (e) {
      console.error('Payment poll error:', e);
      setTimeout(pollPayment, 2000);
    }
  }, [sessionId, refreshUser]);

  useEffect(() => {
    if (!sessionId) { setStatus('error'); return; }
    pollPayment();
  }, [sessionId, pollPayment]);

  return (
    <div className={`min-h-screen flex items-center justify-center px-4 ${isDark ? 'bg-zinc-950' : 'bg-zinc-50'}`}
      style={{ fontFamily: "'Outfit', sans-serif" }}>
      <div className={`w-full max-w-md rounded-2xl p-8 text-center ${isDark ? 'bg-zinc-900 border border-zinc-800' : 'bg-white border border-zinc-200 shadow-xl'}`}>
        {status === 'checking' && (
          <div data-testid="payment-checking">
            <Loader2 size={40} className="animate-spin mx-auto mb-4" style={{ color: '#00D4FF' }} />
            <h2 className="text-xl font-bold mb-2">Processing Payment</h2>
            <p className={`text-sm ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>Verifying your payment with Stripe...</p>
          </div>
        )}
        {status === 'success' && (
          <div data-testid="payment-success">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: '#06D6A0' }}>
              <Check size={28} className="text-white" />
            </div>
            <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
            <p className={`text-sm mb-4 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
              Welcome to <span className="font-bold" style={{ color: '#00D4FF' }}>{txn?.plan_name || 'OmniView'}</span>.
              Your plan is now active.
            </p>
            <button data-testid="go-to-dashboard" onClick={() => navigate('/dashboard')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90"
              style={{ background: 'linear-gradient(135deg,#002FA7,#00D4FF)' }}>
              Go to Dashboard <ArrowRight size={16} />
            </button>
          </div>
        )}
        {(status === 'error' || status === 'expired' || status === 'timeout') && (
          <div data-testid="payment-error">
            <AlertTriangle size={40} className="mx-auto mb-4 text-orange-500" />
            <h2 className="text-xl font-bold mb-2">
              {status === 'expired' ? 'Session Expired' : status === 'timeout' ? 'Verification Timeout' : 'Something Went Wrong'}
            </h2>
            <p className={`text-sm mb-4 ${isDark ? 'text-zinc-400' : 'text-zinc-500'}`}>
              {status === 'timeout' ? 'Your payment may still be processing. Check your email.' : 'Please try again or contact support.'}
            </p>
            <button data-testid="back-to-pricing" onClick={() => navigate('/pricing')}
              className={`px-6 py-2.5 rounded-lg text-sm font-medium border ${isDark ? 'border-zinc-700 text-zinc-300' : 'border-zinc-300 text-zinc-700'}`}>
              Back to Pricing
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
